import { useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { CoiUploader } from "@/customer/coi/CoiUploader";
import { useSetCartWaiver } from "@/customer/cart/hooks";
import type { Cart } from "@/customer/cart/types";

// FR-8 — embedded COI step. Two paths:
//   - Upload now: the CoiUploader returns a coi_documents row id; we
//     surface the "We've received your certificate" affordance and let
//     the customer continue. Delivery won't be released until JDR
//     approves the doc.
//   - Switch to JDR's damage waiver: the customer flips the cart's
//     `damage_waiver_opted_in` flag, which hides this step and adds
//     the waiver line to the quote.
//
// The spec also lets a customer with a current approved COI skip the
// step entirely; in this slice we don't fetch that pre-check (no
// dedicated endpoint), so the section renders whenever the cart needs
// insurance and the waiver isn't in play.
export function CoiSection({
  cart,
  onAdvance
}: {
  cart: Cart;
  onAdvance(): void;
}): JSX.Element {
  const [uploadedId, setUploadedId] = useState<number | null>(null);
  const setWaiver = useSetCartWaiver();

  function handleUploaded(id: number): void {
    setUploadedId(id);
    onAdvance();
  }

  function chooseWaiver(): void {
    setWaiver.mutate(true, { onSuccess: () => onAdvance() });
  }

  if (uploadedId !== null) {
    return (
      <Alert>
        <p className="text-sm font-medium text-brand-black">
          We&rsquo;ve received your certificate of insurance.
        </p>
        <p className="mt-1 text-sm text-zinc-600">
          An admin will review it shortly. You can continue and complete the
          rest of checkout. <strong>Your delivery won&rsquo;t be scheduled
          until your COI is approved.</strong>
        </p>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">
        At least one item in your cart requires insurance. Upload your
        Certificate of Insurance (ACORD 25 PDF/JPG/PNG, ≤25&nbsp;MB), or
        switch to JDR&rsquo;s damage waiver to skip paperwork.
      </p>

      <CoiUploader onUploaded={handleUploaded} />

      <div className="flex items-center gap-3 pt-1 text-sm text-zinc-500">
        <span>or</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={setWaiver.isPending || cart.damage_waiver_opted_in}
          onClick={chooseWaiver}
        >
          {setWaiver.isPending ? "Switching…" : "Use JDR's damage waiver instead"}
        </Button>
      </div>
    </div>
  );
}
